import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";

const STATUS_META = {
  APPROVED:  { label: "Approved",  color: "#007bff", bg: "#cce5ff" },
  EXECUTING: { label: "Executing", color: "#6f42c1", bg: "#e8d5ff" },
  COMPLETED: { label: "Completed", color: "#28a745", bg: "#d4edda" },
  FAILED:    { label: "Failed",    color: "#dc3545", bg: "#fde2e2" },
  REJECTED:  { label: "Rejected",  color: "#6c757d", bg: "#e2e3e5" },
  SNOOZED:   { label: "Snoozed",   color: "#856404", bg: "#fff3cd" },
  PENDING:   { label: "Pending",   color: "#fd7e14", bg: "#fff3cd" },
};

const ACTION_ICON = {
  ROLL_DOWN_CALL:         "↻",
  SQUARE_OFF_ALL_OPTIONS: "✕",
  SQUARE_OFF_STOCKS:      "📉",
  BUY_STOCK:              "＋",
  SQUARE_OFF:             "✕",
};

const TRANSITION_ICON = {
  APPROVED:  "✓",
  REJECTED:  "✕",
  SNOOZED:   "⏸",
  EXECUTING: "⚙",
  COMPLETED: "✔",
  FAILED:    "✖",
  PENDING:   "·",
};

const DAYS_OPTIONS = [7, 14, 30, 90];

export default function HistoryPage() {
  const { selectedAccount } = useAccount();
  const isAllAccounts = selectedAccount === "All Accounts";

  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [filter, setFilter]     = useState("ALL");
  const [days, setDays]         = useState(30);
  const [expanded, setExpanded] = useState({});
  const [msg, setMsg]           = useState(null);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3000);
  };

  const fetchHistory = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const params = { username: selectedAccount, days, limit: 200 };
      if (filter !== "ALL") params.status = filter;
      const res = await axios.get("/automation/history", { params });
      setRows(res.data);
    } catch (e) {
      console.error(e);
      flash("Failed to load history.", false);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, filter, days]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Summary counts from loaded rows
  const counts = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const displayed = rows; // filtering is server-side

  return (
    <div style={{ padding: "20px 25px 40px", background: "#f4f6f9", minHeight: "100vh" }}>

      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>
          {msg.text}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={pageTitle}>Automation History</h2>
          <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
            Full audit trail of every suggestion — decisions, executions, and failures
          </p>
        </div>
        <button onClick={fetchHistory} style={refreshBtn} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {/* Summary tiles */}
      <div style={statsGrid}>
        {[["Total", rows.length, "#2c3e50"], ["Approved", counts.APPROVED || 0, "#007bff"],
          ["Completed", counts.COMPLETED || 0, "#28a745"], ["Rejected", counts.REJECTED || 0, "#6c757d"],
          ["Failed", counts.FAILED || 0, "#dc3545"], ["Snoozed", counts.SNOOZED || 0, "#856404"]
        ].map(([label, value, color]) => (
          <div key={label} style={tile}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>

        {/* Status filter tabs */}
        <div style={filterBar}>
          {["ALL", "APPROVED", "COMPLETED", "REJECTED", "FAILED", "EXECUTING", "SNOOZED"].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              style={{
                ...filterTab,
                background:  filter === s ? "#2c3e50" : "#fff",
                color:       filter === s ? "#fff"    : "#555",
                borderColor: filter === s ? "#2c3e50" : "#dee2e6",
              }}
            >
              {s === "ALL" ? `All (${rows.length})` : `${s} (${counts[s] || 0})`}
            </button>
          ))}
        </div>

        {/* Date range selector */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#888" }}>Show last</span>
          {DAYS_OPTIONS.map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              style={{
                ...filterTab,
                background:  days === d ? "#2c3e50" : "#fff",
                color:       days === d ? "#fff"    : "#555",
                borderColor: days === d ? "#2c3e50" : "#dee2e6",
                padding:     "5px 10px",
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading && rows.length === 0 ? (
        <div style={emptyBox}>Loading history…</div>
      ) : displayed.length === 0 ? (
        <div style={emptyBox}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <div style={{ fontWeight: 600, color: "#555" }}>No history yet</div>
          <div style={{ fontSize: 13, color: "#999", marginTop: 6 }}>
            Approved, rejected, and completed suggestions will appear here
          </div>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          {/* Table header */}
          <div style={tableHeader}>
            <div style={{ flex: "0 0 40px" }} />
            <div style={{ flex: "0 0 180px" }}>Action</div>
            <div style={{ flex: 1 }}>Symbol · Strategy</div>
            {isAllAccounts && <div style={{ flex: "0 0 110px" }}>Account</div>}
            <div style={{ flex: "0 0 130px" }}>Final Status</div>
            <div style={{ flex: "0 0 150px" }}>Triggered</div>
            <div style={{ flex: "0 0 150px" }}>Last Updated</div>
            <div style={{ flex: "0 0 30px" }} />
          </div>

          {displayed.map((row, idx) => (
            <HistoryRow
              key={row.id}
              row={row}
              idx={idx}
              expanded={!!expanded[row.id]}
              onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
              showAccount={isAllAccounts}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── History Row ─────────────────────────────────────────────── */

function HistoryRow({ row, idx, expanded, onToggle, showAccount }) {
  const sm   = STATUS_META[row.status] || STATUS_META.PENDING;
  const icon = ACTION_ICON[row.action_type] || "⚙";

  return (
    <>
      <div
        onClick={onToggle}
        style={{
          display: "flex", alignItems: "center",
          padding: "12px 18px",
          borderTop: idx === 0 ? "none" : "1px solid #f0f0f0",
          cursor: "pointer",
          background: expanded ? "#fafbff" : "transparent",
          transition: "background .1s",
        }}
      >
        {/* Icon */}
        <div style={{ flex: "0 0 40px" }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: sm.bg, color: sm.color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15 }}>
            {icon}
          </div>
        </div>

        {/* Action type */}
        <div style={{ flex: "0 0 180px", fontWeight: 600, fontSize: 13, color: "#2c3e50" }}>
          {row.action_type?.replace(/_/g, " ")}
        </div>

        {/* Symbol · Strategy */}
        <div style={{ flex: 1, fontSize: 13, color: "#555" }}>
          <strong>{row.symbol}</strong>
          {row.strategy_name && (
            <span style={{ color: "#aaa", marginLeft: 6, fontSize: 12 }}>· {row.strategy_name}</span>
          )}
        </div>

        {/* Account — only in All Accounts mode */}
        {showAccount && (
          <div style={{ flex: "0 0 110px" }}>
            {row.username && (
              <span style={{ padding: "2px 8px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                {row.username}
              </span>
            )}
          </div>
        )}

        {/* Status badge */}
        <div style={{ flex: "0 0 130px" }}>
          <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>
            {sm.label}
          </span>
          {row.error_message && (
            <div style={{ fontSize: 11, color: "#dc3545", marginTop: 2 }}>
              {row.error_message.slice(0, 40)}
            </div>
          )}
        </div>

        {/* Created at */}
        <div style={{ flex: "0 0 150px", fontSize: 12, color: "#888" }}>
          {row.created_at?.slice(0, 16)}
        </div>

        {/* Updated at */}
        <div style={{ flex: "0 0 150px", fontSize: 12, color: "#888" }}>
          {row.updated_at?.slice(0, 16) || "—"}
        </div>

        {/* Expand arrow */}
        <div style={{ flex: "0 0 30px", color: "#ccc", textAlign: "right" }}>
          {expanded ? "▲" : "▼"}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding: "0 18px 16px 18px", borderTop: "1px solid #f0f0f0", background: "#fafbff" }}>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>

            {/* Audit trail */}
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={sectionLabel}>Decision Trail</div>
              {row.audit_trail && row.audit_trail.length > 0 ? (
                <div style={timeline}>
                  {row.audit_trail.map((entry, i) => {
                    const em = STATUS_META[entry.to_status] || {};
                    return (
                      <div key={i} style={timelineRow}>
                        <div style={{ ...timelineDot, background: em.color || "#aaa" }}>
                          {TRANSITION_ICON[entry.to_status] || "·"}
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: em.color || "#555" }}>
                            {entry.from_status
                              ? `${entry.from_status} → ${entry.to_status}`
                              : entry.to_status}
                          </span>
                          {entry.note && (
                            <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>
                              {entry.note}
                            </span>
                          )}
                          <div style={{ fontSize: 11, color: "#aaa", marginTop: 1 }}>
                            {entry.actioned_at?.slice(0, 16)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#aaa", marginTop: 6 }}>
                  No audit entries (pre-dates logging)
                </div>
              )}
            </div>

            {/* Payload */}
            {row.metadata && (
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={sectionLabel}>Payload</div>
                <pre style={metaPre}>{JSON.stringify(row.metadata, null, 2)}</pre>
              </div>
            )}

          </div>
        </div>
      )}
    </>
  );
}

/* ── Styles ───────────────────────────────────────────────────── */

const pageTitle  = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const toast      = { position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14 };
const refreshBtn = { padding: "8px 16px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontSize: 13 };
const statsGrid  = { display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12, marginBottom: 20 };
const tile       = { background: "#fff", borderRadius: 10, padding: "14px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const filterBar  = { display: "flex", gap: 6, flexWrap: "wrap" };
const filterTab  = { padding: "6px 12px", borderRadius: 20, border: "1px solid", cursor: "pointer", fontSize: 12, fontWeight: 500 };
const emptyBox   = { background: "#fff", borderRadius: 12, padding: "60px 20px", textAlign: "center", color: "#888", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" };

const tableHeader = { display: "flex", alignItems: "center", padding: "10px 18px", background: "#f8f9fb", borderBottom: "1px solid #eee", fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" };
const statusBadge = { display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 };

const sectionLabel = { fontSize: 11, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", marginBottom: 8, marginTop: 12 };
const metaPre      = { margin: 0, fontSize: 12, color: "#2c3e50", background: "#f0f2f5", borderRadius: 6, padding: "8px 12px", whiteSpace: "pre-wrap", wordBreak: "break-word" };

const timeline    = { display: "flex", flexDirection: "column", gap: 8 };
const timelineRow = { display: "flex", alignItems: "flex-start", gap: 10 };
const timelineDot = { width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 };
